/**
 * Moon painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const moonPaintingIdeasPack: ToilePack = {
    slug: 'moon-painting-ideas',
    category: 'craft',
    name: 'Moon painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'sea', label: 'Moon over sea', subject: 'a canvas of a full moon over a dark sea with a shimmering path of light'},
        {key: 'crescent', label: 'Pink crescent', subject: 'a canvas of a thin crescent moon in a pink and violet dusk sky'},
        {key: 'branch', label: 'Moon branch', subject: 'a canvas of a huge pale moon behind a bare black tree branch'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'easy beginner acrylic with a smooth blended sky and a crisp round moon', palette: 'cool blues and soft violet with a pale white moon', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'knife', label: 'Palette knife', style: 'thick palette-knife impasto with a textured cratered moon in raised paint', palette: 'deep blues and silvery whites laid on in raised strokes', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'glow', label: 'Glowing glaze', style: 'soft glazed acrylic layers with a glowing halo of light around the moon', palette: 'dark night tones glowing into warm cream light', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['blue moon', 'with an owl', 'moon phases'],
    directionHint: 'try: blue moon',
    seo: {
        title: 'Moon Painting Ideas: Full Moon, Crescent, Bare Branch',
        metaDescription: 'Nine moon painting ideas: a full moon over the sea, a thin crescent in a pink sky and a huge moon behind a bare branch, in three styles.',
        h1: 'Moon painting ideas: full moon over the sea, pink crescent, moon behind a bare branch',
        tagline: 'Nine moon canvases, three moon scenes, each in easy acrylic, palette-knife impasto or a glowing glaze.',
        intro: 'The moon is painted for its quiet and its light on the world below. These nine show three scenes: a full moon over a dark sea with a shimmering path of light, a thin crescent in a pink and violet dusk sky, and a huge pale moon behind a bare black branch. Type another moon, such as a blue moon or a moon with an owl, and the same quiet scene carries it.',
        useCases: [
            'a bedroom wall piece',
            'a calm corner above a reading chair',
            'a gift for a night owl',
            'a window sill canvas',
        ],
        faq: [
            {q: 'Which style glows most?', a: 'The glowing glaze shows a halo of warm light around the moon over dark night tones.'},
            {q: 'What is the palette-knife moon?', a: 'A moon with a textured, cratered surface built up in raised paint.'},
            {q: 'Is the moon over the sea a real photograph?', a: 'No. It is a painted canvas of a moon over the sea, made to look like a finished painting.'},
            {q: 'Can I change the moon?', a: 'Yes. Type another moon and the three styles carry it.'},
        ],
    },
}
